import { useState, useEffect } from 'react'
import classNames from 'classnames'
import spritemap from '@milestone-sys/web-design-system/msds-spritemap.svg'
import './table.scss'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable,
  Row
} from '@tanstack/react-table'
import GlobalFilter from './GlobalFilter'
import Button from 'components/Button/Button'
import React from 'react'

type Identifiable = {
  id: number
  name?: string
  formattedCreatedUtc?: string
  formattedUpdatedUtc?: string
  streamName?: string
}

type TableNewProps<TData extends Identifiable> = {
  data: TData[]
  columns: ColumnDef<TData>[]
  handleDeleteDataRow: (rowId: number) => void
  handleDuplicateRow: (rowIndex: number) => void
  disableSearch?: boolean
  disableSort?: boolean
  disablePagination?: boolean
  addButtonLabel?: string
  addButtonLabelIcon?: string
  addButtonSize?: any
  onAddButtonClick?: () => void
  customButtons?: JSX.Element[]
  rowClickBaseUrl?: string
  isDoubleCell?: boolean
  deleteModalDescription?: string
  deleteModalHeadline?: string
  customCssClasses?: string
  getRowCanExpand?
  handleShowSubRow?: number
  renderSubRow?: (props: { row: Row<TData> }) => JSX.Element | null
}

const Table = <TData extends Identifiable>({
  data,
  columns,
  disableSearch = false,
  disableSort = false,
  disablePagination = false,
  addButtonLabel,
  addButtonLabelIcon,
  addButtonSize,
  onAddButtonClick,
  customButtons,
  rowClickBaseUrl,
  isDoubleCell = false,
  customCssClasses,
  getRowCanExpand,
  handleShowSubRow,
  renderSubRow
}: TableNewProps<TData>) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  })
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('')
  const [pageInputValue, setPageInputValue] = useState<string>('1')

  // Sync page input value when pagination changes via buttons
  useEffect(() => {
    setPageInputValue(String(pagination.pageIndex + 1))
  }, [pagination.pageIndex])

  const filteredData = globalFilterValue
    ? data.filter(rowData => {
        const lowerCaseGlobalFilterValue = globalFilterValue.toLowerCase()

        // Check if 'name' contains the newValue (case-insensitive)
        const nameMatch = rowData.name && rowData.name.toLowerCase().includes(lowerCaseGlobalFilterValue)

        // Check if 'formattedCreatedUtc' contains the newValue (case-insensitive)
        const formattedCreatedUtc =
          rowData.formattedCreatedUtc && rowData.formattedCreatedUtc.toLowerCase().includes(lowerCaseGlobalFilterValue)

        // Check if 'formattedUpdatedUtc' contains the newValue (case-insensitive) if we need check this value. right now only site design use it
        let formattedUpdatedUtc = false

        if (columns.some(item => item['accessorKey'] === 'formattedUpdatedUtc')) {
          formattedUpdatedUtc =
            rowData.formattedUpdatedUtc &&
            rowData.formattedUpdatedUtc.toLowerCase().includes(lowerCaseGlobalFilterValue)
        }

        return nameMatch || formattedCreatedUtc || formattedUpdatedUtc
      })
    : data

  const table = useReactTable({
    columns,
    data: filteredData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    enableSorting: !disableSort,
    state: {
      pagination
    },
    getRowCanExpand
  })

  const displayPagination = !disablePagination && filteredData.length > 0

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const inputValue = event.currentTarget.value ? Number(event.currentTarget.value) : 1
      const maxPage = table.getPageCount()
      const isWithinBounds = inputValue >= 1 && inputValue <= maxPage
      if (isWithinBounds) {
        table.setPageIndex(inputValue - 1)
      } else {
        const currentPage = String(pagination.pageIndex + 1)
        setPageInputValue(currentPage)
      }
    }
  }

  return (
    <div className="col-12">
      <div className="row">
        <div className="msds-btn-group col-6 col-md-8">
          {addButtonLabel && (
            <Button
              id={'xsd__e2e-add-button'}
              label={addButtonLabel}
              icon={addButtonLabelIcon || 'plus'}
              size={addButtonSize}
              onClickHandler={onAddButtonClick}
            />
          )}
          {customButtons &&
            customButtons.length > 0 &&
            customButtons.map((button, index) => <React.Fragment key={index}>{button}</React.Fragment>)}
        </div>
        {disableSearch ? null : (
          <div className="col-6 col-md-4 mb-2 search-bar">
            <GlobalFilter filterValue={globalFilterValue} setFilterValue={setGlobalFilterValue} />
          </div>
        )}
      </div>

      <div
        className={classNames(
          'msds-table msds-table--small',
          isDoubleCell && 'msds-table--double-cell',
          customCssClasses && customCssClasses
        )}>
        <table className="msds-table__table-style">
          <thead className="msds-table__head">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id} className="msds-table__head-row">
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className={classNames(
                      'msds-table__header',
                      header.column.id === 'primaryActions' && 'table__primary-actions-header',
                      header.column.id === 'secondaryActions' && 'table__secondary-actions-header',
                      header.column.id === 'name' && 'table__name-header'
                    )}>
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? 'msds-table__header-headline cursor-pointer select-none'
                          : 'msds-table__header-headline',
                        onClick: header.column.getToggleSortingHandler()
                      }}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.id !== 'primaryActions' && header.column.id !== 'secondaryActions' && header.column.getCanSort() ? (
                        <div onClick={header.column.getToggleSortingHandler()}>
                          {header.column.getIsSorted() ? (
                            header.column.getIsSorted() === 'asc' ? (
                              // Custom ascending arrow
                              <span className="msds-table__header-order-icon">
                                <svg className="msds-table__header-acesnding-order-icon">
                                  <use href={spritemap + '#right-arrow'} />
                                </svg>
                              </span>
                            ) : (
                              // Custom descending arrow
                              <span className="msds-table__header-order-icon">
                                <svg className="msds-table__header-descending-order-icon">
                                  <use href={spritemap + '#right-arrow'} />
                                </svg>
                              </span>
                            )
                          ) : (
                            // Custom unsorted arrow
                            <span className="msds-table__header-order-icon">
                              <svg className="msds-table__header-descending-order-icon">
                                <use href={spritemap + '#right-arrow'} />
                              </svg>
                              <svg className="msds-table__header-acesnding-order-icon">
                                <use href={spritemap + '#right-arrow'} />
                              </svg>
                            </span>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="msds-table__body">
            {table.getRowModel().rows.map(row => (
              <React.Fragment key={row.id}>
                {/* Main row */}
                <tr
                  id={'xsd__e2e-table-row-' + row.index}
                  data-camera-group-id={row.original.id}
                  className="msds-table__body-row cursor-pointer">
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className={classNames(
                        'msds-table__body-row-data',
                        cell.column.id === 'primaryActions' && 'msds-table__body-row-data--primary-actions',
                        cell.column.id === 'secondaryActions' && 'msds-table__body-row-data--secondary-actions'
                      )}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>

                {/* Sub Row */}
                {(row.original.streamName || (handleShowSubRow => 0)) && renderSubRow && renderSubRow({ row })}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {displayPagination && <div className="h-2" />}

        {displayPagination && (
          <div className="msds-table__pagination msds-text-gray-10">
            <button
              className="msds-table__pagination-prev-next-buttons msds-btn msds-btn--sm msds-btn--tertiary"
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}>
              {'<<'}
            </button>
            <button
              className="msds-table__pagination-prev-next-buttons msds-btn msds-btn--sm msds-btn--tertiary"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}>
              {'<'}
            </button>
            <span className="msds-table__pagination-indicator d-none d-md-inline-block">
              <span className="msds-table__pagination-indicator d-none d-md-inline-block">
                {'Show'}{' '}
                <strong>
                  {pagination.pageIndex + 1} of {table.getPageCount().toLocaleString()}
                </strong>
              </span>
              <span className="flex items-center gap-1">
                | Go to page:
                <input
                  type="number"
                  min="1"
                  max={table.getPageCount()}
                  value={pageInputValue}
                  onChange={e => setPageInputValue(e.target.value)}
                  onKeyDown={e => handleOnKeyDown(e)}
                  className="msds-input__text-input"
                />
              </span>
            </span>
            <button
              className="msds-table__pagination-prev-next-buttons msds-btn msds-btn--sm msds-btn--tertiary"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}>
              {'>'}
            </button>
            <button
              className="msds-table__pagination-prev-next-buttons msds-btn msds-btn--sm msds-btn--tertiary"
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}>
              {'>>'}
            </button>
            <div className="msds-table__pagination-show-row-numbers">
              <select
                className="msds-select-input__select"
                value={table.getState().pagination.pageSize}
                onChange={e => {
                  table.setPageSize(Number(e.target.value))
                }}>
                {[10, 20, 30, 40, 50].map(pageSize => (
                  <option key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </option>
                ))}
              </select>
              <div className="msds-select-input__icon">
                <svg>
                  <use href={spritemap + '#right-arrow'} />
                </svg>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Table
